import { useEffect, useCallback } from 'react';

type KeyModifier = 'ctrl' | 'meta' | 'alt' | 'shift';

interface ShortcutConfig {
  key: string;
  modifiers?: KeyModifier[];
  action: () => void;
  description?: string;
  enabled?: boolean;
}

interface UseKeyboardShortcutsOptions {
  shortcuts: ShortcutConfig[];
  enabled?: boolean;
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable;
}

function modifierMatches(required: boolean, active: boolean): boolean {
  return required === active;
}

function shortcutMatches(event: KeyboardEvent, shortcut: ShortcutConfig): boolean {
  const modifiers = shortcut.modifiers ?? [];
  return (
    event.key.toLowerCase() === shortcut.key.toLowerCase() &&
    modifierMatches(modifiers.includes('ctrl'), event.ctrlKey) &&
    modifierMatches(modifiers.includes('meta'), event.metaKey) &&
    modifierMatches(modifiers.includes('alt'), event.altKey) &&
    modifierMatches(modifiers.includes('shift'), event.shiftKey)
  );
}

/**
 * Hook to register global keyboard shortcuts
 */
export function useKeyboardShortcuts({ shortcuts, enabled = true }: UseKeyboardShortcutsOptions) {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!enabled) return;

      // Don't trigger shortcuts when typing in inputs
      if (isEditableTarget(event.target) && event.key !== 'Escape') return;

      for (const shortcut of shortcuts) {
        if (shortcut.enabled === false) continue;
        if (shortcutMatches(event, shortcut)) {
          event.preventDefault();
          shortcut.action();
          return;
        }
      }
    },
    [shortcuts, enabled],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);
}

/**
 * Get the display string for a keyboard shortcut
 */
export function getShortcutDisplay(key: string, modifiers?: KeyModifier[]): string {
  const isMac = typeof navigator !== 'undefined' && /Mac/.test(navigator.platform);
  const parts: string[] = [];

  if (modifiers?.includes('ctrl')) {
    parts.push(isMac ? '⌃' : 'Ctrl');
  }
  if (modifiers?.includes('meta')) {
    parts.push(isMac ? '⌘' : 'Win');
  }
  if (modifiers?.includes('alt')) {
    parts.push(isMac ? '⌥' : 'Alt');
  }
  if (modifiers?.includes('shift')) {
    parts.push(isMac ? '⇧' : 'Shift');
  }

  // Format the key nicely
  const formattedKey = key.length === 1 ? key.toUpperCase() : key;
  parts.push(formattedKey);

  return parts.join(isMac ? '' : '+');
}
